import { useState } from "react";
import { ArrowUpRight, Plus, Phone } from "lucide-react";
import { Eyebrow } from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { SITE } from "@/constants/site";

const services = [
    {
        num: "01",
        id: "tax",
        title: "Tax Preparation & Strategic Planning",
        desc: "Filing is the finish line; the real savings happen all year. We prepare personal and business returns with care — and plan ahead so April never surprises you.",
        points: [
            "Individual & small-business returns",
            "Quarterly estimated tax planning",
            "Deduction & credit reviews",
            "Year-round tax strategy, not just a yearly scramble",
        ],
    },
    {
        num: "02",
        id: "bookkeeping",
        title: "Full-Charge Bookkeeping & Payroll",
        desc: "Clean, current books — every month, not every meltdown. We handle the ledger and the payroll runs so you can stay focused on the work only you can do.",
        points: [
            "Monthly bookkeeping & reconciliations",
            "Payroll processing & filings",
            "Catch-up & clean-up for behind-on-books",
            "A simple monthly snapshot of how you're doing",
        ],
    },
    {
        num: "03",
        id: "advisory",
        title: "Business Advisory & Startup Accounting",
        desc: "Starting from scratch? Best time to do it right is day one. We set up your accounting stack, pick the right entity, and give you honest numbers to steer by.",
        points: [
            "Entity selection & setup",
            "Accounting stack & tooling setup",
            "Cash-flow visibility & budgeting",
            "Fractional-CFO-style guidance for founders",
        ],
    },
];

const steps = [
    { num: "01", title: "Say hello", desc: "A free intro call. You talk, we listen, we quote a flat fee in writing." },
    { num: "02", title: "Clean-up & catch-up", desc: "If your books are behind, we bring everything current first." },
    { num: "03", title: "Monthly care", desc: "Books closed, payroll run, questions answered — every month." },
    { num: "04", title: "Year-end victory", desc: "Returns filed early, no scramble, no surprises." },
];

const faqs = [
    {
        q: "What does it cost?",
        a: "Every engagement is quoted as a flat fee, in writing, before any work begins — so you always know exactly what you're paying.",
    },
    {
        q: "My books are a mess. Is that a problem?",
        a: "It's the most common reason people call us. We start with a clean-up phase, bring everything current, and then keep it that way.",
    },
    {
        q: "Do I need to be in Houston to work with you?",
        a: "No. We work securely and paperlessly with clients anywhere, though we're always happy to sit down together in person locally.",
    },
    {
        q: "I just started my business — is it too early?",
        a: "Quite the opposite. Setting up correctly from day one is the cheapest accounting you'll ever do.",
    },
];

function FaqItem({ q, a, index }) {
    const [open, setOpen] = useState(index === 0);
    return (
        <div className="border-b border-ink/10" data-testid="faq-item">
            <button
                data-testid={`faq-toggle-${index}`}
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
                aria-expanded={open}
            >
                <span className="font-serif text-xl font-medium sm:text-2xl">
                    {q}
                </span>
                <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/15 transition-transform duration-200 ${
                        open ? "rotate-45 bg-gold text-cream" : ""
                    }`}
                >
                    <Plus className="h-4 w-4" />
                </span>
            </button>
            {open && (
                <p className="max-w-2xl pb-6 text-sm leading-relaxed text-clay sm:text-base">
                    {a}
                </p>
            )}
        </div>
    );
}

export default function Services() {
    return (
        <div data-testid="page-services">
            <section className="border-b border-ink/10 bg-bone">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <Eyebrow>Services</Eyebrow>
                    <h1 className="mt-6 max-w-2xl font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl">
                        Straightforward work,{" "}
                        <span className="italic text-gold-deep">
                            done warmly.
                        </span>
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-clay sm:text-lg">
                        Three core services, one flat fee each, quoted before
                        we start. Here's exactly what that looks like.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                {services.map((s, i) => (
                    <article
                        key={s.id}
                        className={`border-t border-ink/10 py-14 sm:py-16 ${
                            i === services.length - 1 ? "border-b" : ""
                        }`}
                        data-testid={`service-detail-${s.id}`}
                    >
                        <p className="font-mono text-[11px] tracking-[0.25em] text-gold-deep">
                            {s.num}
                        </p>
                        <h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                            {s.title}
                        </h2>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-clay">
                            {s.desc}
                        </p>
                        <ul className="mt-6 space-y-2.5">
                            {s.points.map((p) => (
                                <li
                                    key={p}
                                    className="flex items-start gap-3 text-sm text-ink"
                                >
                                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </article>
                ))}
            </section>

            <section className="border-y border-ink/10 bg-bone">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <Eyebrow>How it works</Eyebrow>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium tracking-tight sm:text-4xl">
                        From first call to year-end victory.
                    </h2>
                    <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {steps.map((s) => (
                            <div
                                key={s.num}
                                className="border-t-2 border-ink/15 pt-5"
                                data-testid="service-process-step"
                            >
                                <p className="font-mono text-[11px] tracking-[0.25em] text-gold-deep">
                                    {s.num}
                                </p>
                                <h3 className="mt-2 font-serif text-xl font-medium">
                                    {s.title}
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-clay">
                                    {s.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                <Eyebrow>Questions</Eyebrow>
                <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight sm:text-4xl">
                    Asked often, answered honestly.
                </h2>
                <div className="mt-8 border-t border-ink/10">
                    {faqs.map((f, i) => (
                        <FaqItem key={i} q={f.q} a={f.a} index={i} />
                    ))}
                </div>
                <a
                    href={SITE.phoneHref}
                    data-testid="faq-call-btn"
                    className="mt-8 flex w-fit items-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-cream transition-colors hover:bg-gold-deep"
                >
                    <Phone className="h-4 w-4" />
                    Still curious? Call {SITE.phoneDisplay}
                    <ArrowUpRight className="h-4 w-4" />
                </a>
            </section>

            <CTABanner />
        </div>
    );
}
